import { type Store, createLens } from '@frontend/viewer-core';

import type { PointerTracker } from './pointerTracker';
import type {
  MarqueeController,
  MeasureManager,
  SelectionManager,
  ToolId,
  ToolManager,
} from './types';
import { type DxfSessionState } from './types';

export const createToolSlice = (state: Store<DxfSessionState>): Store<ToolId> =>
  createLens(
    state,
    (current) => current.tool,
    (current, tool) => ({ ...current, tool }),
  );

type Dependencies = {
  tool: Store<ToolId>;
  selection: Pick<SelectionManager, 'clear'>;
  measure: Pick<MeasureManager, 'clear'>;
  marqueeController: Pick<MarqueeController, 'abort'>;
  pointerTracker: Pick<PointerTracker, 'reset'>;
};

export const createToolController = ({
  tool,
  selection,
  measure,
  marqueeController,
  pointerTracker,
}: Dependencies): ToolManager => ({
  getState: tool.getState,
  subscribe: tool.subscribe,
  set(next) {
    // Bail out entirely on a redundant call: a tool switch is a change of
    // intent, but re-picking the tool already active is not one, and must not
    // cancel a gesture or wipe pointer feedback that had nothing to do with it.
    if (tool.getState() === next) {
      return;
    }

    tool.setState(() => next);
    // Switching tools discards both, per the spec: neither a selection nor a
    // half-finished measurement means anything under a new tool.
    selection.clear();
    measure.clear();
    marqueeController.abort();
    pointerTracker.reset();
  },
});
